import Model from 'src/models/model'
import { computed, ref } from 'vue'

const ACTIONS = {
  DISABLE_SALES: 'disable_sales',
  ENABLE_SALES: 'enable_sales'
}

function getAllDirtyModels (isDisabledStateRef) {
  // Return the models whose sales disabled states don't match our tracked
  // states. For example, if a state is toggled twice it will match, so it won't
  // be dirty and no action needs to be taken with it.
  return _(isDisabledStateRef.value).values().map(({ model, isDisabled }) => model.salesDisabled !== isDisabled ? model : null).compact().value()
}

function resetIsDisabledState (isDisabledStateRef) {
  isDisabledStateRef.value = {}
}

export function useDisableSales () {
  const editModeRef = ref(false)
  const isDisabledStateRef = ref({})

  function toggleEditMode () {
    if (editModeRef.value) {
      resetIsDisabledState(isDisabledStateRef)
    }

    editModeRef.value = !editModeRef.value
  }

  function areSalesEnabled (model) {
    // If we're not tracking the state of this model, the model is the source of truth.
    if (_.isNil(isDisabledStateRef.value[model.id])) {
      return !model.salesDisabled
    }

    // Otherwise, our tracked state is the source of truth.
    return !isDisabledStateRef.value[model.id].isDisabled
  }

  function toggleSalesDisabled (model) {
    if (!editModeRef.value) {
      return
    }

    if (_.isNil(model.salesDisabled)) {
      console.warn(`Unable to edit model of type ${model.typeName} because it has no salesDisabled property.`)
      return
    }

    // Set the initial state of the model, if it doesn't exist
    isDisabledStateRef.value[model.id] ||= {
      model,
      isDisabled: model.salesDisabled
    }

    // Toggle the state
    const currentIsDisabledState = isDisabledStateRef.value[model.id].isDisabled
    isDisabledStateRef.value[model.id].isDisabled = !currentIsDisabledState
  }

  const hasEdits = computed(() => !_.isEmpty(getAllDirtyModels(isDisabledStateRef)))

  async function saveEdits () {
    const dirtyModels = getAllDirtyModels(isDisabledStateRef)

    // Save all models
    await Model.configuration.batchSave(async () => {
      const saveMutations = _(dirtyModels).map(model => {
        model.action = model.salesDisabled ? ACTIONS.ENABLE_SALES : ACTIONS.DISABLE_SALES
        return model.save()
      }).value()

      await Promise.allSettled(saveMutations)

      _(dirtyModels).each(model => model.resetField('action'))
    })

    resetIsDisabledState(isDisabledStateRef)
    editModeRef.value = false
  }

  return {
    // Vue unpacks refs for us automatically
    editMode: editModeRef,
    toggleEditMode,
    areSalesEnabled,
    toggleSalesDisabled,
    hasEdits,
    saveEdits
  }
}
